/**
 * Dashboard Home Page
 * Main dashboard view with stats and onboarding for providers
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth, useLanguage, API } from "@/context/AppContext";
import {
  Clock, AlertCircle, XCircle, AlertTriangle, Sparkles, Check, CheckCircle,
  ChevronRight, Mail, Plus, MapPin, Star, Briefcase, Trash2
} from "lucide-react";
import { AdsBanner } from "@/components/AdsBanner";
import { HeroCarousel } from "@/components/HeroCarousel";
import { SuperCategories } from "@/components/SuperCategories";
import { TrustBadges, HowItWorks } from "@/components/HomeSections";
import ExpertDashboardHome from "@/components/ExpertDashboardHome";
import { InlineMarketingColumn } from "@/components/InlineMarketingColumn";

const DashboardHome = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [recentJobs, setRecentJobs] = useState([]);
  const [providerProfile, setProviderProfile] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const navigate = useNavigate();

  // Check if provider has completed onboarding
  const isProfileComplete = providerProfile?.categories?.length > 0 || providerProfile?.description;
  const hasSubscription = user?.subscription;
  const isOnboardingComplete = isProfileComplete && hasSubscription;
  const isPendingApproval = user?.role === 'provider' && user?.approval_status === 'pending';
  const isRejected = user?.role === 'provider' && user?.approval_status === 'rejected';

  useEffect(() => {
    axios.get(`${API}/stats/dashboard`, { withCredentials: true })
      .then(res => setStats(res.data))
      .catch(console.error);

    if (user?.role === 'client') {
      axios.get(`${API}/jobs/my`, { withCredentials: true })
        .then(res => setRecentJobs(res.data.slice(0, 5)))
        .catch(console.error);
    } else {
      axios.get(`${API}/jobs?status=open&limit=5`, { withCredentials: true })
        .then(res => setRecentJobs(res.data))
        .catch(console.error);
      
      // Fetch provider profile
      axios.get(`${API}/users/${user?.user_id}`, { withCredentials: true })
        .then(res => setProviderProfile(res.data?.provider_profile))
        .catch(console.error);
    }
  }, [user]);

  // Show pending approval message for unapproved providers
  if (isPendingApproval) {
    return (
      <div className="max-w-2xl mx-auto space-y-8" data-testid="pending-approval">
        <div className="text-center">
          <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Clock className="w-10 h-10 text-yellow-600" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Registrierung wird geprüft
          </h1>
          <p className="text-gray-600 text-lg">
            Ihre Registrierung wurde erfolgreich eingereicht und wird von unserem Team überprüft.
          </p>
        </div>

        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-yellow-100 rounded-full">
                <AlertCircle className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <h3 className="font-semibold text-yellow-800 mb-2">Ausstehende Genehmigung</h3>
                <p className="text-yellow-700 text-sm">
                  Um die Sicherheit unserer Plattform zu gewährleisten, werden alle Dienstleister-Registrierungen 
                  von unserem Team überprüft. Dieser Prozess dauert in der Regel 1-2 Werktage.
                </p>
                <p className="text-yellow-700 text-sm mt-2">
                  Sie erhalten eine E-Mail-Benachrichtigung, sobald Ihre Registrierung genehmigt wurde.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg">Was Sie in der Zwischenzeit tun können</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span className="text-gray-700">Ihr Profil vervollständigen</span>
              <Button 
                variant="outline" 
                size="sm" 
                className="ml-auto"
                onClick={() => navigate('/dashboard/settings')}
              >
                Profil bearbeiten
              </Button>
            </div>
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span className="text-gray-700">Unsere AGB und Datenschutzrichtlinien lesen</span>
              <Button 
                variant="outline" 
                size="sm" 
                className="ml-auto"
                onClick={() => navigate('/agb')}
              >
                AGB lesen
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show rejected message for rejected providers
  if (isRejected) {
    return (
      <div className="max-w-2xl mx-auto space-y-8" data-testid="registration-rejected">
        <div className="text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-10 h-10 text-red-600" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Registrierung abgelehnt
          </h1>
          <p className="text-gray-600 text-lg">
            Leider konnte Ihre Registrierung nicht genehmigt werden.
          </p>
        </div>

        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-red-800 mb-2">Ablehnungsgrund</h3>
                <p className="text-red-700 text-sm">
                  {user?.rejection_reason || 'Bitte kontaktieren Sie unseren Support für weitere Informationen.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-gray-200">
          <CardContent className="p-6 text-center">
            <p className="text-gray-600 mb-4">
              Wenn Sie Fragen haben oder glauben, dass dies ein Fehler ist, kontaktieren Sie bitte unseren Support.
            </p>
            <Button 
              className="bg-[#0044CC] hover:bg-[#003399]"
              onClick={() => window.location.href = 'mailto:support@infirmly.ch'}
            >
              <Mail className="w-4 h-4 mr-2" />
              Support kontaktieren
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show provider onboarding if not complete (but NOT for admin users)
  if (user?.role === 'provider' && !isOnboardingComplete && !user?.is_admin) {
    return (
      <div className="max-w-2xl mx-auto space-y-8" data-testid="provider-onboarding">
        {/* Welcome Header */}
        <div className="text-center">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Sparkles className="w-10 h-10 text-[#0044CC]" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {t('welcomeToInfirmly')}
          </h1>
          <p className="text-gray-600 text-lg">
            {t('welcomeSubtitle')}
          </p>
        </div>

        {/* Setup Steps */}
        <Card className="border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {t('completeSetup')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Step 1: Complete Profile */}
            <div 
              className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                isProfileComplete 
                  ? 'border-green-500 bg-green-50' 
                  : 'border-gray-200 hover:border-[#0044CC]/50 hover:bg-blue-50/30'
              }`}
              onClick={() => !isProfileComplete && navigate('/dashboard/settings')}
              data-testid="step-profile"
            >
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  isProfileComplete ? 'bg-green-500' : 'bg-[#0044CC]'
                }`}>
                  {isProfileComplete ? (
                    <Check className="w-5 h-5 text-white" />
                  ) : (
                    <span className="text-white font-bold">1</span>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">
                    {isProfileComplete ? t('profileCompleted') : t('completeProfile')}
                  </h3>
                  <p className="text-sm text-gray-500">{t('completeProfileDesc')}</p>
                </div>
                {!isProfileComplete && (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                )}
              </div>
            </div>

            {/* Step 2: Choose Subscription */}
            <div 
              className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                hasSubscription 
                  ? 'border-green-500 bg-green-50' 
                  : 'border-gray-200 hover:border-[#0044CC]/50 hover:bg-blue-50/30'
              }`}
              onClick={() => !hasSubscription && navigate('/dashboard/subscription')}
              data-testid="step-subscription"
            >
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  hasSubscription ? 'bg-green-500' : 'bg-gray-300'
                }`}>
                  {hasSubscription ? (
                    <Check className="w-5 h-5 text-white" />
                  ) : (
                    <span className="text-white font-bold">2</span>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">
                    {hasSubscription ? t('subscriptionActive') : t('chooseSubscription')}
                  </h3>
                  <p className="text-sm text-gray-500">{t('chooseSubscriptionDesc')}</p>
                </div>
                {!hasSubscription && (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                )}
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex gap-3">
            <Button 
              onClick={() => {
                if (!isProfileComplete) {
                  navigate('/dashboard/settings');
                } else if (!hasSubscription) {
                  navigate('/dashboard/subscription');
                }
              }}
              className="flex-1 bg-[#0044CC] hover:bg-[#003399] text-white h-11 rounded-full"
              data-testid="continue-setup-btn"
            >
              {t('continueSetup')}
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Show welcome dialog when onboarding is just completed
  if (user?.role === 'provider' && isOnboardingComplete && showWelcome) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="welcome-complete">
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {t('setupComplete')}
            </h2>
            <p className="text-gray-600 mb-6">{t('setupCompleteDesc')}</p>
            <Button 
              onClick={() => setShowWelcome(false)}
              className="bg-[#0044CC] hover:bg-[#003399] text-white h-11 px-8 rounded-full"
            >
              {t('getStartedProvider')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-home">
      {/* Infirmly dark hero — Layout 1 for clients */}
      {user?.role === 'client' && (
        <div
          className="rounded-3xl p-5 sm:p-6 border border-white/10 space-y-5"
          style={{ background: 'linear-gradient(135deg, #0A1A3E 0%, #0F2557 100%)' }}
          data-testid="client-dark-home"
        >
          <div>
            <p className="text-blue-200/70 text-xs uppercase tracking-widest font-semibold">
              {t('hello') || 'Hello'}, {user?.name}
            </p>
            <h1 className="text-white text-2xl sm:text-3xl font-extrabold mt-1 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {t('findTrustedHeader') || 'Find Trusted Swiss Experts'}
            </h1>
          </div>
          <HeroCarousel language={language} />
          <SuperCategories language={language} onSelect={(c) => navigate(`/dashboard/jobs/new?category=${c.id}`)} />
          <TrustBadges language={language} />
          <HowItWorks language={language} />
        </div>
      )}

      {/* Layout 2 for providers/experts */}
      {user?.role === 'provider' && (
        <ExpertDashboardHome user={user} language={language} />
      )}

      {/* Ads Banner - above recent jobs */}
      <AdsBanner />

      {/* Recent Jobs + Marketing — side by side (2-col on lg+) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <div
            className="rounded-3xl p-5 sm:p-6 border border-white/10 text-white h-full"
            style={{ background: 'linear-gradient(135deg, #0A1A3E 0%, #0F2557 100%)' }}
            data-testid="recent-jobs-dark"
          >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {user?.role === 'provider' ? t('newJobs') : t('recentJobs')}
          </h2>
          {user?.role === 'client' && (
            <Button
              onClick={() => navigate('/dashboard/jobs/new')}
              size="sm"
              className="bg-[#0044CC] hover:bg-[#0055EE] text-white rounded-full h-9"
              data-testid="create-job-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> {t('createNewJob')}
            </Button>
          )}
        </div>

        <div className="space-y-2">
          {recentJobs.length === 0 ? (
            <div className="text-center py-8 bg-white/[0.03] border border-white/10 rounded-2xl">
              <Briefcase className="w-8 h-8 text-blue-200/40 mx-auto mb-2" />
              <p className="text-blue-100/60 text-sm">{t('noJobs')}</p>
            </div>
          ) : (
            recentJobs.map((job) => (
              <div
                key={job.job_id}
                className="w-full bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-[#60a5fa]/30 rounded-2xl p-4 transition-all"
                data-testid={`job-row-${job.job_id}`}
              >
                <div className="flex justify-between items-start gap-3">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/dashboard/jobs/${job.job_id}`)}
                    onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/dashboard/jobs/${job.job_id}`); }}
                    className="flex-1 min-w-0 text-left cursor-pointer"
                  >
                    <h3 className="font-semibold text-white text-sm truncate">{job.title}</h3>
                    <p className="text-blue-100/60 text-xs line-clamp-1 mt-0.5">{job.description}</p>
                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                      <span className="text-[11px] text-blue-100/50 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {job.location?.city || t('switzerland')}
                      </span>
                      {job.budget_max && (
                        <span className="text-[11px] font-semibold text-[#60a5fa]">
                          CHF {job.budget_max}
                        </span>
                      )}
                      {/* Rating display */}
                      {job.rating ? (
                        <span className="text-[11px] text-yellow-300 flex items-center gap-0.5">
                          <Star className="w-3 h-3 fill-yellow-300" />
                          {Number(job.rating).toFixed(1)}
                          {job.review_count > 0 && <span className="text-blue-100/40 ml-0.5">({job.review_count})</span>}
                        </span>
                      ) : job.status === 'completed' && user?.role === 'client' ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/jobs/${job.job_id}?rate=1`); }}
                          className="text-[11px] text-yellow-300 hover:text-yellow-200 flex items-center gap-0.5 font-medium"
                          data-testid={`rate-${job.job_id}`}
                        >
                          <Star className="w-3 h-3" />
                          {t('rateJob') || 'Bewerten'}
                        </button>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge className={`text-[10px] ${
                      job.status === 'open' ? 'bg-green-500/20 text-green-300 border border-green-400/30' :
                      job.status === 'in_progress' ? 'bg-blue-500/20 text-blue-300 border border-blue-400/30' :
                      'bg-white/10 text-blue-100 border border-white/10'
                    }`}>
                      {job.status === 'open' && t('open')}
                      {job.status === 'in_progress' && t('inProgress')}
                      {job.status === 'completed' && t('completed')}
                    </Badge>

                    {/* Delete (client only, only for open jobs they own) */}
                    {user?.role === 'client' && job.status === 'open' && (
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!window.confirm('Diesen Auftrag wirklich löschen?')) return;
                          try {
                            await axios.delete(`${API}/jobs/${job.job_id}`, { withCredentials: true });
                            setRecentJobs(rj => rj.filter(j => j.job_id !== job.job_id));
                          } catch (err) {
                            alert(err.response?.data?.detail || 'Löschen fehlgeschlagen');
                          }
                        }}
                        className="w-7 h-7 rounded-lg bg-red-500/15 border border-red-400/30 hover:bg-red-500/30 flex items-center justify-center transition-colors"
                        title="Löschen"
                        data-testid={`delete-job-${job.job_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-300" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
          </div>
        </div>
        <div className="lg:col-span-1">
          <InlineMarketingColumn />
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
